import { z } from "zod";

import {
  adminCategorySchema,
  beadMaterialProfileSchema,
  beadKindSchema,
  catalogSpecSchema,
  hexColorSchema,
} from "./catalog.js";
import { isoDateTimeSchema, millimeterSchema, moneyFenSchema, uuidSchema } from "./primitives.js";

/**
 * 运营后台的请求与响应结构。
 *
 * 后台是**写目录的唯一入口**：小程序只读，所有价格、库存、上下架都从这里改。
 * 因此这里的校验必须严格——目录写错会一路错到下单，而客户端没有纠正能力。
 */

/** 后台角色。首版只有店主与运营，权限差异体现在能否管理账号与结算。 */
export const adminRoleSchema = z.enum(["owner", "operator"]);

export const adminLoginRequestSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(1).max(128),
});

export type AdminLoginRequestPayload = z.infer<typeof adminLoginRequestSchema>;

/** 后台账号的公开信息。绝不包含口令或口令散列。 */
export const adminAccountSchema = z.object({
  id: uuidSchema,
  username: z.string().min(1).max(64),
  displayName: z.string().min(1).max(64),
  role: adminRoleSchema,
});

export type AdminAccountPayload = z.infer<typeof adminAccountSchema>;

export const adminLoginResponseSchema = z.object({
  token: z.string().min(32),
  /** 会话到期时间。到期后客户端必须重新登录，不得静默续期。 */
  expiresAt: isoDateTimeSchema,
  account: adminAccountSchema,
});

export type AdminLoginResponsePayload = z.infer<typeof adminLoginResponseSchema>;

/** 分类列表响应。后台新建分类后返回最新列表，界面据此直接刷新，不额外再拉一次。 */
export const adminCategoryListResponseSchema = z.array(adminCategorySchema);

export type AdminCategoryListResponsePayload = z.infer<typeof adminCategoryListResponseSchema>;

/** 上架状态。下架不影响已有订单引用，只是不再出现在小程序目录里。 */
export const catalogStatusSchema = z.enum(["active", "inactive"]);

export type CatalogStatus = z.infer<typeof catalogStatusSchema>;

export const adminSpecSchema = catalogSpecSchema.extend({
  status: catalogStatusSchema,
});

export type AdminSpecPayload = z.infer<typeof adminSpecSchema>;

export const adminStoneSchema = z.object({
  id: uuidSchema,
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  categoryId: uuidSchema,
  category: z.string().min(1).max(32),
  kind: beadKindSchema,
  showcaseMaterial: beadMaterialProfileSchema.nullable(),
  color: hexColorSchema,
  status: catalogStatusSchema,
  sortOrder: z.int(),
  photoPath: z.string().min(1).nullable(),
  specs: z.array(adminSpecSchema),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export type AdminStonePayload = z.infer<typeof adminStoneSchema>;

export const adminStoneListResponseSchema = z.object({
  items: z.array(adminStoneSchema),
  total: z.int().nonnegative(),
});

export type AdminStoneListResponsePayload = z.infer<typeof adminStoneListResponseSchema>;

/** 新建石种时的一档规格。skuId 由服务端生成，客户端不得指定。 */
export const createSpecRequestSchema = z.object({
  diameterMm: millimeterSchema.positive(),
  stringPathMm: millimeterSchema.positive(),
  priceFen: moneyFenSchema.nonnegative(),
  stock: z.int().nonnegative(),
});

export type CreateSpecRequestPayload = z.infer<typeof createSpecRequestSchema>;

export const createStoneRequestSchema = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  categoryId: uuidSchema,
  kind: beadKindSchema,
  showcaseMaterial: beadMaterialProfileSchema,
  color: hexColorSchema,
  specs: z.array(createSpecRequestSchema).min(1).max(20),
});

export type CreateStoneRequestPayload = z.infer<typeof createStoneRequestSchema>;

/** 改石种基础信息。规格单独改，避免一次请求里既改基础又改价格导致部分失败说不清。 */
export const updateStoneRequestSchema = z.object({
  name: z.string().min(1).max(64).optional(),
  categoryId: uuidSchema.optional(),
  kind: beadKindSchema.optional(),
  showcaseMaterial: beadMaterialProfileSchema.optional(),
  color: hexColorSchema.optional(),
  status: catalogStatusSchema.optional(),
  sortOrder: z.int().optional(),
});

export type UpdateStoneRequestPayload = z.infer<typeof updateStoneRequestSchema>;

export const updateSpecRequestSchema = z.object({
  diameterMm: millimeterSchema.positive().optional(),
  stringPathMm: millimeterSchema.positive().optional(),
  priceFen: moneyFenSchema.nonnegative().optional(),
  stock: z.int().nonnegative().optional(),
  status: catalogStatusSchema.optional(),
});

export type UpdateSpecRequestPayload = z.infer<typeof updateSpecRequestSchema>;

/**
 * 通用素材上传结果。
 *
 * 商品照片、作品主图与实拍图、设计师头像、首页大图都走这一个上传口：
 * 上传得到地址，再把地址写到各自的字段上。地址由内容哈希决定、不可覆盖。
 */
export const uploadMediaResponseSchema = z.object({
  photoPath: z.string().min(1),
  contentHash: z.string().min(1),
  byteSize: z.int().positive(),
});

export type UploadMediaResponsePayload = z.infer<typeof uploadMediaResponseSchema>;

export const createCategoryRequestSchema = z.object({
  name: z.string().min(1).max(32),
});

export type CreateCategoryRequestPayload = z.infer<typeof createCategoryRequestSchema>;

/**
 * 后台商品列表的筛选参数。
 *
 * 来自 URL 查询串，因此全部是字符串，用 coerce 转成目标类型；
 * 上限写进契约而不是散在控制器里，后台与服务端对「最多一页多少条」的理解才一致。
 */
export const adminStoneListQuerySchema = z.object({
  keyword: z.string().min(1).max(64).optional(),
  categoryId: uuidSchema.optional(),
  status: catalogStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type AdminStoneListQueryPayload = z.infer<typeof adminStoneListQuerySchema>;
